import { memo, useCallback } from 'react';
import { ButtonMain } from '@quero-delivery/quero-components-web';
import { format } from '@querodelivery/qd-date-helper';

import { DELIVERY_STATUS_HELPER } from '@/static/DeliveryStatusTypes';

import styles from './DeliveryCard.module.scss';
import type { DeliveryAction } from '@/hooks/delivery/useDeliveryAction';
import useDeliveryAction from '@/hooks/delivery/useDeliveryAction';
import ConnectDelivery from '../connectDelivery/ConnectDelivery';
import CancelDelivery from '../cancelDelivery/CancelDelivery';
import { useAppDispatch } from '@/_store/hooks';
import type { Delivery } from '@/_features/deliveries/types';
import { setSelectedDeliveryId } from '@/_store/modules/deliveries';
import { STATUS_ENTREGA } from '@/Constants';
import { useCities } from '@/_hooks/use-cities';

interface DeliveryCardProps {
  delivery: Delivery;
  rechargeDeliveries?(): void;
}

function DeliveryCard({ delivery, rechargeDeliveries }: DeliveryCardProps) {
  const dispatch = useAppDispatch();

  const { getCityInfo } = useCities();

  const cityInfo = getCityInfo(delivery.municipioId);

  const status = DELIVERY_STATUS_HELPER[delivery.status];

  const {
    availableActions,
    triggerDeliveryAction,
    actionLoading,
    connectDeliveryToMotoboy,
    cancelDelivery,
  } = useDeliveryAction(delivery, rechargeDeliveries);

  function handleClickCard() {
    dispatch(setSelectedDeliveryId(delivery._id));
  }

  const renderActionButton = useCallback(
    (action: DeliveryAction) => {
      const isLoading = actionLoading === action.key;
      const disabled = !!actionLoading;

      switch (action.key) {
        case 'ASSOCIAR':
          return (
            <ConnectDelivery
              key={action.key}
              delivery={delivery}
              connectDeliveryToMotoboy={(motoboyId: string) =>
                connectDeliveryToMotoboy(action, motoboyId)
              }
              isActionLoading={isLoading}
              disabled={disabled}
            />
          );
        case 'CANCELAR':
          return (
            <CancelDelivery
              isFinishedDelivery={delivery.status === STATUS_ENTREGA.FINALIZADO}
              key={action.key}
              cancelDelivery={(collectTaxOfPlace, passOnTaxForMotoboy) =>
                cancelDelivery(action, collectTaxOfPlace, passOnTaxForMotoboy)
              }
              isActionLoading={isLoading}
              disabled={disabled}
            />
          );
        default:
          return (
            <ButtonMain
              key={action.key}
              textFirst={action.text}
              tertiary
              onClick={event => {
                event.stopPropagation();
                triggerDeliveryAction(action);
              }}
              loading={isLoading}
              disabled={disabled}
            />
          );
      }
    },
    [availableActions, actionLoading],
  );

  return (
    <div className={styles.deliveryCard} onClick={handleClickCard}>
      <div className={styles.headerCard}>
        <b>Código {delivery.codigo}</b>
        <b>
          <time dateTime={delivery.createdAt}>
            {format(new Date(delivery.createdAt), 'dd/MM/yyyy')}
          </time>
        </b>
      </div>
      <div className={styles.containerInfo}>
        <div className={styles.info}>
          <span>Estabelecimento </span>
          <strong>{delivery.nomeEstabelecimento}</strong>
        </div>
        {cityInfo ? (
          <div className={styles.info}>
            <span>Franquia </span>
            <strong>{`${cityInfo.nome} - ${cityInfo.uf}`}</strong>
          </div>
        ) : null}
        <div className={styles.info}>
          <span>Forma de pagamento </span>
          <strong>{delivery.formaPagamento}</strong>
        </div>

        <div className={styles.info}>
          {availableActions.map(action => renderActionButton(action))}
          <span>Status </span>
          <strong className={styles.status}>{status.text}</strong>
        </div>
      </div>
    </div>
  );
}

export default memo(DeliveryCard, (prevProps, nextProps) => {
  const prevDelivery = prevProps.delivery;
  const nextDelivery = nextProps.delivery;

  if (
    prevDelivery._id === nextDelivery._id &&
    prevDelivery.status === nextDelivery.status &&
    prevDelivery.updatedAt === nextDelivery.updatedAt &&
    prevDelivery?.page === nextDelivery?.page
  ) {
    return true;
  }

  return false;
});
